import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import Decimal from 'decimal.js';
import produce from 'immer';
import { serviceModel } from '..';
import { DiscountModel } from '../DiscountModel';
import { PriceIncreaseModel } from '../PriceIncreaseModel';
import * as serviceBillingModel from '../service-billing';
import { makeBillUpfrontState } from '../service-billing/makers';
import { RecurrencePeriodTypes } from '../service-billing/model';
import * as serviceCostModel from '../service-cost';
import { makeAmendedStatus, makeApprovedStatus, makeAwaitingApprovalStatus, makeCanceledStatus, makeCompletedStatus, makeTerminatedStatus, makeChanges, makeMandatoryParticipation, makeOptionalParticipation, makeOneTimeManualStatus, makeOnApprovalStatus, makeOneTimeInStagesStatus } from './makers';
import { ToggledField, toggleNewOngoingBilling } from './newOngoingBilling';
import { effectiveDate, isAmended, isAwaitingApproval, isCompleted, changes, startBillingAt } from './selectors';
export function setBillingTrigger(service, billingTrigger) {
    return _object_spread_props(_object_spread({}, service), {
        billing: billingTrigger
    });
}
export function setBillUpfront(service, billUpfront) {
    return produce(service, (s)=>{
        if (serviceBillingModel.isOngoing(s.billing) && billUpfront !== s.billing.isBilledUpfront) {
            if (isAwaitingApproval(s.status)) {
                return toggleNewOngoingBilling(service, ToggledField.IsBilledUpfront);
            } else {
                s.billing.isBilledUpfront = billUpfront;
            }
        }
    });
}
export function setSkipBillingOnAcceptance(service, skipBillingOnAcceptance, featureFlags) {
    return produce(service, (s)=>{
        if ((featureFlags.START_BILLING_AT || featureFlags.START_BILLING_AT_PHASE_2_INLINE) && startBillingAt(service) && !skipBillingOnAcceptance) {
            skipBillingOnAcceptance = true;
        }
        if (serviceBillingModel.isOngoing(s.billing) && skipBillingOnAcceptance !== s.billing.billUpfrontState.skipBillingOnAcceptance) {
            if (isAwaitingApproval(s.status)) {
                return toggleNewOngoingBilling(service, ToggledField.BillOnAcceptance);
            } else {
                s.billing.billUpfrontState = makeBillUpfrontState(skipBillingOnAcceptance);
            }
        }
    });
}
export function setBillingDayOfWeek(service, billingDayOfWeek) {
    const newBilling = serviceBillingModel.setBillingDayOfWeek(service.billing, billingDayOfWeek);
    return newBilling !== service.billing ? _object_spread_props(_object_spread({}, service), {
        billing: newBilling
    }) : service;
}
export function setBillingDayOfMonth(service, billingDayOfMonth) {
    const newBilling = serviceBillingModel.setBillingDayOfMonth(service.billing, billingDayOfMonth);
    return newBilling !== service.billing ? _object_spread_props(_object_spread({}, service), {
        billing: newBilling
    }) : service;
}
export function setBillingDayOfYear(service, billingDayOfYear) {
    const newBilling = serviceBillingModel.setBillingDayOfYear(service.billing, billingDayOfYear);
    return newBilling !== service.billing ? _object_spread_props(_object_spread({}, service), {
        billing: newBilling
    }) : service;
}
export function setMaxCharges(service, enabled, limit) {
    let chargesLeft = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : limit;
    const newBilling = serviceBillingModel.setMaxCharges(service.billing, enabled, limit, chargesLeft);
    return newBilling !== service.billing ? _object_spread_props(_object_spread({}, service), {
        billing: newBilling
    }) : service;
}
export function setIntegratedClass(service, integratedClass) {
    return _object_spread_props(_object_spread({}, service), {
        integrations: service.integrations.setQboIntegratedClass(integratedClass)
    });
}
export function deleteMaxCharges(service) {
    const newBilling = serviceBillingModel.deleteMaxCharges(service.billing);
    return newBilling !== service.billing ? _object_spread_props(_object_spread({}, service), {
        billing: newBilling
    }) : service;
}
export function setPaused(service, isPaused) {
    return produce(service, (s)=>{
        if (serviceBillingModel.isOngoing(s.billing) && isPaused !== s.billing.isPaused) {
            if (isAwaitingApproval(s.status)) {
                return toggleNewOngoingBilling(service, ToggledField.IsPaused);
            } else {
                s.billing.isPaused = isPaused;
            }
        }
    });
}
export function setProrated(service, isProrated) {
    return produce(service, (s)=>{
        if (serviceBillingModel.isOngoing(s.billing) && isProrated !== s.billing.isProrated) {
            if (isAwaitingApproval(s.status)) {
                return toggleNewOngoingBilling(service, ToggledField.IsProrated);
            } else {
                s.billing.isProrated = isProrated;
            }
        }
    });
}
export function setParticipation(service, optional, includedByDefault) {
    return _object_spread_props(_object_spread({}, service), {
        participation: optional ? makeOptionalParticipation(includedByDefault) : makeMandatoryParticipation()
    });
}
export function setTerminated(service) {
    return _object_spread_props(_object_spread({}, service), {
        status: makeTerminatedStatus(effectiveDate(service) || new Date())
    });
}
export function setCanceled(service) {
    return _object_spread_props(_object_spread({}, service), {
        status: makeCanceledStatus()
    });
}
export function setAwaitingApproval(service, options) {
    return _object_spread_props(_object_spread({}, service), {
        status: makeAwaitingApprovalStatus(options)
    });
}
export function discardChanges(service) {
    if (isAmended(service.status)) {
        return _object_spread_props(_object_spread({}, service), {
            status: makeApprovedStatus({
                date: effectiveDate(service) || new Date(),
                effectiveDate: startBillingAt(service)
            })
        });
    } else if (isCompleted(service.status)) {
        return _object_spread_props(_object_spread({}, service), {
            status: _object_spread_props(_object_spread({}, service.status), {
                completed: {
                    effectiveDate: startBillingAt(service),
                    effectiveDateSource: null
                }
            })
        });
    } else {
        return service;
    }
}
export function setChanges(service, changes) {
    if (isAmended(service.status)) {
        return _object_spread_props(_object_spread({}, service), {
            status: makeAmendedStatus(_object_spread_props(_object_spread({
                date: service.status.date
            }, service.status.amended), {
                changes
            }))
        });
    } else if (isAwaitingApproval(service.status)) {
        return _object_spread_props(_object_spread({}, service), {
            status: makeAmendedStatus({
                date: effectiveDate(service),
                approvalMethod: service.status.awaitingApproval.approvalMethod,
                amendedOn: service.status.awaitingApproval.addedOn,
                changes,
                note: service.status.awaitingApproval.note
            })
        });
    } else if (isCompleted(service.status)) {
        return _object_spread_props(_object_spread({}, service), {
            status: makeCompletedStatus(service.status.date, changes)
        });
    } else {
        return _object_spread_props(_object_spread({}, service), {
            status: makeAmendedStatus({
                changes,
                date: effectiveDate(service)
            })
        });
    }
}
export function setBillingChanges(service, billing) {
    const existingChanges = changes(service);
    const newChanges = existingChanges ? _object_spread_props(_object_spread({}, existingChanges), {
        billing
    }) : makeChanges({
        billing
    });
    return setChanges(service, newChanges);
}
export function setBillingDayOfWeekChanges(service, dayOfWeek) {
    const newBilling = serviceBillingModel.setBillingDayOfWeek(applyChanges(service).billing, dayOfWeek);
    return setBillingChanges(service, newBilling);
}
export function setBillingDayOfMonthChanges(service, dayOfMonth) {
    const newBilling = serviceBillingModel.setBillingDayOfMonth(applyChanges(service).billing, dayOfMonth);
    return setBillingChanges(service, newBilling);
}
export function setBillingDayOfYearChanges(service, dayOfYear) {
    const newBilling = serviceBillingModel.setBillingDayOfYear(applyChanges(service).billing, dayOfYear);
    return setBillingChanges(service, newBilling);
}
export function setIncludedServicesChanges(service, includedServices) {
    const newPrice = includedServices.reduce((acc, service)=>acc.plus(service.rate), new Decimal(0));
    const existingChanges = changes(service);
    const initialCost = (existingChanges === null || existingChanges === void 0 ? void 0 : existingChanges.cost) || service.cost;
    const newCost = serviceCostModel.match(initialCost, {
        fixed: (cost)=>_object_spread_props(_object_spread({}, cost), {
                price: newPrice
            }),
        variable: (cost)=>_object_spread_props(_object_spread({}, cost), {
                price: newPrice
            }),
        range: (cost)=>_object_spread_props(_object_spread({}, cost), {
                maxPrice: newPrice
            })
    });
    const newBundle = {
        services: includedServices
    };
    const newChanges = existingChanges ? _object_spread_props(_object_spread({}, existingChanges), {
        bundle: newBundle,
        cost: newCost
    }) : makeChanges({
        bundle: newBundle,
        cost: newCost
    });
    return setChanges(service, newChanges);
}
export function setCost(service, cost) {
    const updatedService = _object_spread_props(_object_spread({}, service), {
        cost
    });
    let result = updatedService;
    if (!cost.discount) {
        const serviceWithoutDiscountExpiration = setDiscountPeriodExpiration(updatedService, undefined);
        result = setLimitedTimeDiscountOffer(serviceWithoutDiscountExpiration, undefined);
    }
    return result;
}
export function setCostChanges(service, cost) {
    const existingChanges = changes(service);
    const newChanges = existingChanges ? _object_spread_props(_object_spread({}, existingChanges), {
        cost
    }) : makeChanges({
        cost
    });
    return setChanges(service, newChanges);
}
export function setPriceIncreaseChanges(service, priceIncrease) {
    const newCost = serviceCostModel.setPriceIncrease(applyChanges(service).cost, priceIncrease);
    return setCostChanges(service, newCost);
}
export function setPreapprovedAmountChanges(service, amount, controlledByAdvancedSettings) {
    const newCost = serviceCostModel.setPreapprovedAmount(applyChanges(service).cost, amount, controlledByAdvancedSettings);
    return setCostChanges(service, newCost);
}
export function setNote(service, note) {
    if (isAmended(service.status)) {
        return _object_spread_props(_object_spread({}, service), {
            status: _object_spread_props(_object_spread({}, service.status), {
                amended: _object_spread_props(_object_spread({}, service.status.amended), {
                    note
                })
            })
        });
    } else if (isAwaitingApproval(service.status)) {
        return _object_spread_props(_object_spread({}, service), {
            status: _object_spread_props(_object_spread({}, service.status), {
                awaitingApproval: _object_spread_props(_object_spread({}, service.status.awaitingApproval), {
                    note
                })
            })
        });
    }
    return service;
}
export function applyChanges(service) {
    return isAmended(service.status) ? applyChangesOnService(service, service.status.amended.changes) : isCompleted(service.status) ? applyChangesOnService(service, service.status.completed.changes) : service;
}
function applyChangesOnService(service, changes) {
    if (!changes) return service;
    return _object_spread_props(_object_spread({}, service), {
        name: changes.name != null ? changes.name : service.name,
        description: changes.description != null ? changes.description : service.description,
        billing: changes.billing != null ? changes.billing : service.billing,
        cost: changes.cost != null ? changes.cost : service.cost,
        bundle: changes.bundle != null ? changes.bundle : service.bundle
    });
}
export function setPriceIncrease(service, priceIncrease) {
    return _object_spread_props(_object_spread({}, service), {
        cost: serviceCostModel.setPriceIncrease(service.cost, priceIncrease)
    });
}
export function setDiscount(service, discount) {
    return _object_spread_props(_object_spread({}, service), {
        cost: serviceCostModel.setDiscount(service.cost, discount)
    });
}
export function setDiscountChanges(service, discount) {
    const newCost = serviceCostModel.setDiscount(applyChanges(service).cost, discount);
    return setCostChanges(service, newCost);
}
export function setPrice(service, cost) {
    return _object_spread_props(_object_spread({}, service), {
        cost: serviceCostModel.setPrice(service.cost, cost)
    });
}
export function setPriceChanges(service, cost) {
    const newCost = serviceCostModel.setPrice(applyChanges(service).cost, cost);
    return setCostChanges(service, newCost);
}
function setServiceIntegration(service, setIntegration) {
    if (service.bundle) {
        return _object_spread_props(_object_spread({}, service), {
            bundle: _object_spread_props(_object_spread({}, service.bundle), {
                services: service.bundle.services.map((s)=>_object_spread_props(_object_spread({}, s), {
                        integrations: setIntegration(s)
                    }))
            })
        });
    }
    return _object_spread_props(_object_spread({}, service), {
        integrations: setIntegration(service)
    });
}
export function setFinancialCentsIntegration(service, financialCents, options) {
    return setServiceIntegration(service, (s)=>s.integrations.setFinancialCentsIntegration(financialCents, options));
}
export function setKarbonIntegration(service, karbon, options) {
    return setServiceIntegration(service, (s)=>s.integrations.setKarbonIntegration(karbon, options));
}
export function setLimitedTimeDiscountOffer(service, limitedTimeDiscountOffer) {
    if (isAwaitingApproval(service.status)) {
        return _object_spread_props(_object_spread({}, service), {
            status: _object_spread_props(_object_spread({}, service.status), {
                awaitingApproval: _object_spread_props(_object_spread({}, service.status.awaitingApproval), {
                    limitedTimeDiscountOffer: limitedTimeDiscountOffer
                })
            })
        });
    }
    return service;
}
export function setOnApproval(service) {
    const updatedService = _object_spread_props(_object_spread({}, service), {
        billing: serviceBillingModel.makeOnApprovalBilling(),
        status: makeOnApprovalStatus(service)
    });
    const discountExpiration = service.cost.discount ? serviceCostModel.discountPeriodExpiration({
        discount: service.cost.discount
    }) : undefined;
    let result = updatedService;
    if (discountExpiration === null || discountExpiration === void 0 ? void 0 : discountExpiration.hasExpiration()) {
        result = setDiscountPeriodExpiration(updatedService, undefined);
    }
    const priceIncrease = serviceModel.priceIncrease(service);
    if (priceIncrease.exists()) {
        result = setPriceIncrease(result, PriceIncreaseModel.makeNone());
    }
    return result;
}
export function setOneTimeInStages(service, oneTimeInStages) {
    const updatedService = _object_spread_props(_object_spread({}, service), {
        billing: serviceBillingModel.makeOneTimeInStagesBilling(oneTimeInStages),
        status: makeOneTimeInStagesStatus(service)
    });
    const discountExpiration = service.cost.discount ? serviceCostModel.discountPeriodExpiration({
        discount: service.cost.discount
    }) : undefined;
    let result = updatedService;
    if (discountExpiration === null || discountExpiration === void 0 ? void 0 : discountExpiration.hasExpiration()) {
        result = setDiscountPeriodExpiration(updatedService, undefined);
    }
    const priceIncrease = serviceModel.priceIncrease(service);
    if (priceIncrease.exists()) {
        result = setPriceIncrease(result, PriceIncreaseModel.makeNone());
    }
    return result;
}
export function setOneTimeManual(service) {
    const updatedService = _object_spread_props(_object_spread({}, service), {
        status: makeOneTimeManualStatus(service),
        billing: serviceBillingModel.makeOneTimeManualBilling()
    });
    const discountExpiration = service.cost.discount ? serviceCostModel.discountPeriodExpiration({
        discount: service.cost.discount
    }) : undefined;
    let result = updatedService;
    if (discountExpiration === null || discountExpiration === void 0 ? void 0 : discountExpiration.hasExpiration()) {
        result = setDiscountPeriodExpiration(updatedService, undefined);
    }
    const priceIncrease = serviceModel.priceIncrease(service);
    if (priceIncrease.exists()) {
        result = setPriceIncrease(result, PriceIncreaseModel.makeNone());
    }
    return result;
}
export function setOngoing(service) {
    return _object_spread_props(_object_spread({}, service), {
        billing: serviceBillingModel.makeOngoingBilling(serviceBillingModel.recurrencePeriod(service.billing) || RecurrencePeriodTypes.Monthly, {
            isAwaitingApproval: isAwaitingApproval(service.status),
            billingDayOfMonth: serviceBillingModel.billingDayOfMonth(service.billing),
            billingDayOfWeek: serviceBillingModel.billingDayOfWeek(service.billing),
            billingDayOfYear: serviceBillingModel.billingDayOfYear(service.billing),
            isBilledUpfront: serviceBillingModel.isBilledUpfront(service.billing),
            maxCharges: serviceBillingModel.maxCharges(service.billing),
            billUpfrontState: makeBillUpfrontState(true)
        })
    });
}
export function setRepeatableManual(service) {
    let _options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
    return _object_spread_props(_object_spread({}, service), {
        billing: serviceBillingModel.makeRepeatableManualBilling(serviceBillingModel.recurrencePeriod(service.billing) || RecurrencePeriodTypes.Monthly, {
            maxCharges: serviceBillingModel.maxCharges(service.billing)
        }),
        status: serviceModel.makeRepeatableManualStatus(service)
    });
}
export function setDiscountPeriodExpiration(service, discountPeriodExpiration) {
    let discount = service.cost.discount;
    if (discount) {
        discount = discount.setExpiration(discountPeriodExpiration);
    }
    if (isAwaitingApproval(service.status)) {
        return _object_spread_props(_object_spread({}, service), {
            cost: serviceCostModel.setDiscount(service.cost, discount)
        });
    }
    return setDiscountChanges(service, discount);
}
export function setPreapprovedAmount(service, amount, controlledByAdvancedSettings) {
    return _object_spread_props(_object_spread({}, service), {
        cost: serviceCostModel.setPreapprovedAmount(service.cost, amount, controlledByAdvancedSettings)
    });
}
export const setStartBillingAt = (service, startBillingAt)=>{
    return toggleNewOngoingBilling(service, ToggledField.StartBillingAt, {
        effectiveDate: startBillingAt !== null && startBillingAt !== void 0 ? startBillingAt : undefined
    });
};
export function setIncludedServices(service, includedServices) {
    const newPrice = includedServices.reduce((acc, service)=>acc.plus(service.rate), new Decimal(0));
    return _object_spread_props(_object_spread({}, service), {
        cost: serviceCostModel.match(service.cost, {
            fixed: (cost)=>_object_spread_props(_object_spread({}, cost), {
                    price: newPrice
                }),
            variable: (cost)=>_object_spread_props(_object_spread({}, cost), {
                    price: newPrice
                }),
            range: (cost)=>cost
        }),
        bundle: {
            services: includedServices
        }
    });
}
export function setBundlePrice(service, includedServiceRates, bundleRate, discountAmount) {
    if (isAwaitingApproval(service.status)) {
        var _service_cost_discount;
        const newDiscount = discountAmount ? DiscountModel.makePercent(discountAmount, (_service_cost_discount = service.cost.discount) === null || _service_cost_discount === void 0 ? void 0 : _service_cost_discount.expiration()) : null;
        const newCost = serviceCostModel.setPrice2(serviceCostModel.setDiscount(service.cost, newDiscount), bundleRate);
        const newService = setCost(service, newCost);
        if (!newService.bundle) {
            return newService;
        }
        const newIncludedServices = applyNewIncludedServiceRates(newService.bundle.services, includedServiceRates);
        return setIncludedServices(newService, newIncludedServices);
    } else {
        var _serviceWithChanges_cost_discount;
        const serviceWithChanges = applyChanges(service);
        const newDiscount = discountAmount ? DiscountModel.makePercent(discountAmount, (_serviceWithChanges_cost_discount = serviceWithChanges.cost.discount) === null || _serviceWithChanges_cost_discount === void 0 ? void 0 : _serviceWithChanges_cost_discount.expiration()) : null;
        const newCost = serviceCostModel.setPrice2(serviceCostModel.setDiscount(serviceWithChanges.cost, newDiscount), bundleRate);
        const newService = setCostChanges(service, newCost);
        if (!newService.bundle) {
            return newService;
        }
        const newIncludedServices = applyNewIncludedServiceRates(newService.bundle.services, includedServiceRates);
        return setIncludedServicesChanges(newService, newIncludedServices);
    }
}
function applyNewIncludedServiceRates(services, includedServiceRates) {
    return services.map((service)=>{
        const rate = includedServiceRates[service.serviceTemplateId || ''];
        return _object_spread_props(_object_spread({}, service), {
            rate: rate >= 0 ? new Decimal(rate) : service.rate
        });
    });
}
